import React, { FC, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import useDarkMode from '@hooks/use-dark-mode'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@components/ui/card'
import Loader from '@components/ui/loader'
import { useMutation, useQuery } from '@connectrpc/connect-query'
import {
  exchange,
  issueEmailVerificationChallenge,
  redeemGoogleOAuthCode,
  whoami,
} from '@gen/api/v1/api-ApiService_connectquery'
import { LoginView } from '@lib/views'
import { setAccessToken } from '@lib/auth'
import { parseErrorMessage } from '@lib/errors'
import { toast } from 'sonner'

const GoogleOAuthCallbackPage: FC = () => {
  const darkMode = useDarkMode()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const exchangeMutation = useMutation(exchange)
  const issueEmailVerificationChallengeMutation = useMutation(
    issueEmailVerificationChallenge,
  )
  const redeemGoogleOAuthCodeMutation = useMutation(redeemGoogleOAuthCode)
  const { data: whoamiRes, refetch: refetchWhoami } = useQuery(whoami)

  useEffect(() => {
    ;(async () => {
      const code = searchParams.get('code')
      const state = searchParams.get('state')

      if (code && state) {
        try {
          // Redeem the Google OAuth code.
          await redeemGoogleOAuthCodeMutation.mutateAsync({
            code,
            redirectUrl: `${window.location.origin}/google-oauth-callback`,
            state,
          })

          // Fetch the whoami query to determine if the user has verified their email.
          const { data: whoamiRes } = await refetchWhoami()

          // If the user has verified their email, navigate to the organizations page.
          if (whoamiRes?.intermediateSession?.emailVerified) {
            const { accessToken } = await exchangeMutation.mutateAsync({})
            setAccessToken(accessToken)

            navigate('/')
            return
          }

          // Issue a new email verification challenge.
          await issueEmailVerificationChallengeMutation.mutateAsync({})

          // Navigate to the email verification page.
          navigate(`/login?view=${LoginView.VerifyEmailAddress}`)
        } catch (error) {
          const message = parseErrorMessage(error)
          toast.error('Could not verify Google OAuth code', {
            description: message,
          })

          setTimeout(() => {
            navigate('/login')
          }, 2500)
        }
      }
    })()
  }, [])

  return (
    <div className={darkMode ? 'dark' : ''}>
      <main className="bg-background flex items-center justify-center min-h-screen w-screen">
        <Card className="max-w-sm">
          <CardHeader>
            <CardTitle>Google OAuth Verification</CardTitle>
            <CardDescription>Verifying your Google account...</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <Loader />
          </CardContent>
        </Card>
      </main>
    </div>
  )
}

export default GoogleOAuthCallbackPage
